import React, { useState } from 'react';
import { Input, Button, Typography, Form, message, Spin, Alert } from 'antd';
import { LockOutlined, UserOutlined, LoginOutlined, UserAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../redux/authSlice';
import { register } from '../data/auth';
import type { AppDispatch, RootState } from '../redux';

const { Title, Paragraph } = Typography;

const Auth: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { status, error } = useSelector((state: RootState) => state.auth);

    const [mode, setMode] = useState<'login' | 'register'>('login');
    const [localError, setLocalError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async ({ email, password }: { email: string; password: string }) => {
        setLocalError(null);
        if (mode === 'login') {
            try {
                await dispatch(login({ email, password })).unwrap();
                message.success('Login successful!');
                navigate('/');
            } catch (err: any) {
                setLocalError(err.message || 'Login failed. Please check your credentials.');
            }
        } else {
            setLoading(true);
            try {
                await register({ email, password });
                message.success('Registration successful! You can now log in.');
                setMode('login');
                form.resetFields();
            } catch (err: any) {
                const errMsg = err?.response?.data?.detail || err.message || 'Registration failed';
                setLocalError(errMsg);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="auth-page-container">
            <div className="auth-box">
                <Title level={2}>{mode === 'login' ? 'Sign In' : 'Register'}</Title>
                <Paragraph>
                    {mode === 'login' ? 'Enter your credentials to access the platform' : 'Create your account'}
                </Paragraph>

                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[{ required: true, message: 'Please enter your email' }]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            placeholder="Enter your email"
                            autoComplete="username"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Please enter your password' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={mode === 'login' ? 'Enter your password' : 'Choose a password'}
                            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        />
                    </Form.Item>

                    {(localError || error) && (
                        <Alert
                            message={localError || error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            icon={mode === 'login' ? <LoginOutlined /> : <UserAddOutlined />}
                            loading={status === 'loading' || loading}
                            className="btn btn-primary"
                        >
                            {mode === 'login' ? 'Log In' : 'Register'}
                        </Button>
                    </Form.Item>
                </Form>

                {(status === 'loading' || loading) && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                        <Spin />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Auth;
